import { useDispatch } from "react-redux";
import { discussionHandlerMap } from "../discussionLogics"; // Import from index
import { useSelector } from "react-redux";
import { selectUserToken } from "../../../features/userSlice";
import { useEffect, useMemo, useRef, useState } from "react";
import { setCommentsLoader } from "../../../features/genericCommentsAndFilesSlice";
export const useComments = () => {
  const dispatch = useDispatch();
  const oauthTokenHeader = useSelector(selectUserToken) as string;
  const oauthTokenRef = useRef<string>(oauthTokenHeader);
  useEffect(() => {
    oauthTokenRef.current = oauthTokenHeader;
  }, [oauthTokenHeader]);

  const [commentList, setCommentList] = useState<any>([]);
  const [hasFetched, setHasFetched] = useState(false);

  const { deleteLoader, isCommentLoading, deleteArray } = useSelector(
    (state: any) => state.genericCommentsAndFiles
  );

  // We use a ref to keep the latest comments accessible inside async callbacks (polling, timeouts)
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const commentListRef = useRef<any[]>([]);
  /**
   * setCommentListAndRef
   * ---------------------
   * A wrapper around React's setState that also writes the same value to a ref.
   * This keeps React state (for rendering) and a mutable ref (for async access)
   * always in sync.
   *
   * Accepts either a value or an updater function like the standard state setter.
   *
   * Example:
   * setCommentListAndRef(prev => [...prev, newComment]);
   * -> Updates UI and ensures commentListRef.current === updated array immediately.
   */

  const setCommentListAndRef = (valueOrUpdater: any) => {
    setCommentList((prev: any[]) => {
      const next =
        typeof valueOrUpdater === "function"
          ? valueOrUpdater(prev)
          : valueOrUpdater;
      commentListRef.current = next;
      return next;
    });
  };
  const moduleType = useSelector(
    (state: any) => state.genericCommentsAndFiles.moduleType
  ) as keyof typeof discussionHandlerMap; // get the module type from the redux store

  const objectType = useSelector(
    (state: any) => state.genericCommentsAndFiles.objectType
  ) as keyof typeof discussionHandlerMap; // get the object type from the redux store

  const shared_uid = useSelector(
    (state: any) => state.genericCommentsAndFiles.objectSharedUid
  );

  const object_id = useSelector(
    (state: any) => state.genericCommentsAndFiles.commentObjectID
  ); // get the specific object id of the comment being edited or deleted or adding new file(All Forecast Tab)

  const multiMetaSharedUid = useSelector(
    (state: any) => state.genericCommentsAndFiles.multiMetaShearedUid
  );

  // Convert shared_uid to an array if it's a single string(when fetching comments for single / multiple object_shared_uid)
  const object_shared_uid: string[] = useMemo(() => {
    if (Array.isArray(multiMetaSharedUid) && multiMetaSharedUid.length > 0) {
      return multiMetaSharedUid; // AllData tab
    }
    return shared_uid ? [shared_uid] : []; // SingleData tab
  }, [shared_uid, multiMetaSharedUid]);

  const handlers = discussionHandlerMap[moduleType]; // Get the handlers for the module type like ActionItem/BusinessInsight

  /**
   * parseModified
   * -------------
   * Given a comment object `c`, returns a numeric timestamp (ms) representing
   * either `c.modified` (if present) or `c.created` (fallback). If no valid
   * date is found, returns null.
   *
   * Example input:
   * { created: '2025-10-09 09:30:00' } -> returns Date.parse('2025-10-09 09:30:00')
   * { created: '2025-10-09 09:30:00', modified: '2025-10-09 09:45:00' } -> returns modified timestamp
   */

  const parseModified = (c: any) => {
    if (!c) return null;
    const s = c.modified ?? c.created;
    if (!s) return null;
    const t = Date.parse(s);
    return isNaN(t) ? null : t;
  };

  /**
   * getLatestModifiedTime
   * ---------------------
   * Reads the latest (newest) modified/created time from commentListRef.current
   * and returns it as a server-friendly string "YYYY-MM-DD HH:mm:ss". If there
   * are no valid timestamps, returns null.
   *
   * Example:
   * commentListRef.current = [
   * { shared_uid: 'A', created: '2025-10-09 09:30:00' },
   * { shared_uid: 'B', modified: '2025-10-09 09:50:00' }
   * ]
   * getLatestModifiedTime() => '2025-10-09 09:50:00'
   */

  const getLatestModifiedTime = () => {
    const arr = commentListRef.current ?? [];
    if (!arr.length) return null;
    const millis = arr.map(parseModified).filter((t) => t !== null) as number[];
    if (!millis.length) return null;
    const max = Math.max(...millis);
    return new Date(max).toISOString().slice(0, 19).replace("T", " ");
  };

  /**
   * mergeUpsertComments
   * --------------------
   * Merge existing local comments and incoming comments from the server.
   * - Adds new comments (not present locally)
   * - Replaces existing local comment only if the incoming item is newer (or equal)
   * - Returns an array sorted newest-first (by modified/created)
   *
   * Example:
   * existing = [A@09:00, B@09:05, C@09:10]
   * incoming = [B@09:08 (updated), D@09:12 (new)]
   * result = [D@09:12, C@09:10, B@09:08, A@09:00]
   */

  const mergeUpsertComments = (existing: any[], incoming: any[]) => {
    // build map of existing
    const map = new Map<string, any>();
    for (const e of existing) {
      map.set(e.shared_uid, { ...e }); // clone to avoid accidental mutation
    }

    for (const inc of incoming) {
      const curr = map.get(inc.shared_uid);
      if (!curr) {
        // new item
        map.set(inc.shared_uid, { ...inc });
      } else {
        // replace only if incoming is newer (defensive), otherwise still replace to keep server source of truth
        const currTs = parseModified(curr) ?? 0;
        const incTs = parseModified(inc) ?? 0;
        if (incTs >= currTs) {
          map.set(inc.shared_uid, { ...inc });
        }
      }
    }

    // to array and sort: newest first (by modified or created)
    return Array.from(map.values()).sort((a, b) => {
      const ta = parseModified(a) ?? 0;
      const tb = parseModified(b) ?? 0;
      return tb - ta;
    });
  };

  /**
   * fetchComments
   * -------------
   * Fetch comments from the server. When `isPolling === true`, we call
   * handlers.fetchComments with `last_time` (the latest local timestamp) —
   * the server should return only comments newer than `last_time`.
   *
   * Behavior summary:
   * - non-polling (initial/manual) => show loader, fetch full list, replace local
   * - polling & server returned items => merge/upsert the incoming items
   */

  const fetchComments = async (isPolling: boolean = false) => {
    const token = oauthTokenRef.current;
    if (isPolling && (deleteLoader || isCommentLoading)) return;
    if (!handlers?.fetchComments) return;
    if (!isPolling) {
      dispatch(setCommentsLoader(true));
    }
    try {
      const last_time = isPolling ? getLatestModifiedTime() : undefined;
      await handlers.fetchComments(
        token,
        objectType,
        object_shared_uid,
        async (newComments: any) => {
          if (isPolling && last_time) {
            if (Array.isArray(newComments) && newComments.length > 0) {
              setCommentListAndRef((prev: any) =>
                mergeUpsertComments(prev ?? [], newComments)
              );
            }
            // If polling and server returned [], do nothing — no silent full fetch
            return;
          } else {
            // Initial/manual (non-polling) => replace local list with server-provided list
            setCommentListAndRef(
              (newComments ?? []).map((c: any) => ({ ...c }))
            );
          }
        },
        last_time
      );
    } catch (error) {
      dispatch({ type: "error", payload: { message: error } });
    } finally {
      if (!isPolling) {
        setHasFetched(true);
        dispatch(setCommentsLoader(false));
      }
    }
  };
  //-- when clickin on POST button to create new comment
  const addComment = async (commentBody: any) => {
    //dispatch(setCommentsLoader(true));
    try {
      const commentRes = await handlers.postComment(
        oauthTokenHeader,
        commentBody,
        shared_uid
      );

      if (commentRes?.reason !== "Created.") {
        throw new Error(`Failed to add comment.`);
      }
      await handlers.fetchComments(
        oauthTokenHeader,
        objectType,
        object_shared_uid,
        setCommentList
      );
      //  else {
      //   dispatch(
      //     setDisplayErrorSnackBar({
      //       message: "Comment has been updated",
      //       type: "success",
      //     })
      //   );
      // }
    } catch (error) {
      dispatch({ type: "error", payload: { message: error } });
    } finally {
      // dispatch(setCommentsLoader(false));
    }
  };

  const handleUpdateComment = async (commentBody: any) => {
    if (!handlers?.updateComment) return; // prevent call if handler is undefined
    try {
      const updateRes = await handlers.updateComment(
        oauthTokenHeader,
        object_id,
        commentBody
      );
      if (updateRes?.reason !== "Success.") {
        throw new Error(`Failed to update comment.`);
      }
      // Fetch updated comments after posting
      await handlers.fetchComments(
        oauthTokenHeader,
        objectType,
        object_shared_uid,
        setCommentList
      );
    } catch (error: any) {
      dispatch({
        type: "error",
        payload: { message: error?.parsedText || "Unknown error occurred" },
      });
    } finally {
      //dispatch(setIsLoading(false));
      //dispatch(setCommentsLoader(true));
    }
  };

  // helper: figure out match by shared_uid or id (covers both shapes)
  const matchesComment = (c: any, id: string | number) =>
    c?.shared_uid === id || c?.id === id;

  const handleDeleteComment = async (commentId: any) => {
    if (!handlers?.deleteComment) return; // was updateComment before, but we delete here

    try {
      const delRes = await handlers.deleteComment(
        oauthTokenHeader,
        object_id,
        commentId
      );

      if (delRes?.reason !== "Success.") {
        throw new Error(`Failed to delete comment.`);
      }

      // ✅ Locally remove the deleted comment and update UI (no refetch)
      setCommentListAndRef((prev: any[]) =>
        (prev ?? []).filter((c) => !matchesComment(c, commentId))
      );

      // (optional) toast/snackbar here for success
    } catch (error: any) {
      dispatch({
        type: "error",
        payload: { message: error?.parsedText || "Unknown error occurred" },
      });
    }
  };

  //-- Fetch Comments (if present)
  useEffect(() => {
    if (!object_shared_uid?.length || !objectType || !moduleType) return;

    const timeout = setTimeout(() => {
      fetchComments(false); // Initial fetch
    }, 200);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line
  }, [object_shared_uid, objectType, moduleType]);

  // Polling effect - starts after initial fetch
  useEffect(() => {
    // Preconditions
    if (
      !hasFetched ||
      !object_shared_uid?.length ||
      !objectType ||
      !moduleType
    ) {
      return;
    }

    // If we're in a "busy" state, stop polling
    if (deleteLoader || isCommentLoading) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return; // do not start interval while busy
    }

    // Start (or restart) polling when not busy
    pollingIntervalRef.current = setInterval(() => {
      // Extra safety: guard again in case flags flipped between ticks
      if (!deleteLoader && !isCommentLoading) {
        fetchComments(true);
      }
    }, 10000);

    // Cleanup
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
    // 👇 include loader flags so effect pauses/resumes immediately
    // eslint-disable-next-line
  }, [
    hasFetched,
    object_shared_uid,
    objectType,
    moduleType,
    deleteLoader,
    isCommentLoading,
  ]);

  useEffect(() => {
    if (!deleteArray || deleteArray.length === 0) return;

    setCommentListAndRef((prev: any[]) => {
      const deleteSet = new Set(deleteArray);
      const filtered = (prev ?? []).filter(
        (comment) => !deleteSet.has(comment.shared_uid)
      );
      if (filtered.length !== prev.length) {
        return filtered;
      }
      return prev;
    });
  }, [deleteArray]);

  return {
    commentList,
    addComment,
    fetchComments,
    handleUpdateComment,
    handleDeleteComment,
    hasFetched,
  };
};
